// EmailDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Calendar,
  User,
  Paperclip,
  Shield,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Download,
} from "lucide-react";
import api from "../../services/api";

const EmailDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadMessage();
  }, [id]);

  const loadMessage = async () => {
    setLoading(true);
    try {
      const response = await api.getEmailMessageById(id);
      setMessage(response);
    } catch (error) {
      console.error("Failed to load message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async () => {
    setAnalyzing(true);
    try {
      await api.reanalyzeEmail(id);
      loadMessage();
    } catch (error) {
      console.error("Failed to analyze message:", error);
    } finally {
      setAnalyzing(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: "bg-red-500/10 text-red-400 border-red-500/20",
      high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      medium: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      low: "bg-green-500/10 text-green-400 border-green-500/20",
    };
    return colors[priority] || colors.medium;
  };

  const getRiskColor = (risk) => {
    const colors = {
      critical: "bg-red-600/10 text-red-400 border-red-600/20",
      high: "bg-red-500/10 text-red-400 border-red-500/20",
      medium: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      low: "bg-green-500/10 text-green-400 border-green-500/20",
    };
    return colors[risk] || colors.low;
  };

  const getSentimentIcon = (sentiment) => {
    const score = message.analysis?.sentimentScore || 0;
    if (score > 0.5) return "😊";
    if (score > 0) return "🙂";
    if (score === 0) return "😐";
    if (score > -0.5) return "😟";
    return "😞";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
        <div className="p-4 lg:p-6">
          <div className="text-center py-20">
            <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-300 mb-4">Email not found</p>
            <button
              onClick={() => navigate("/email/messages")}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Messages
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/email/messages")}
            className="flex items-center gap-2 px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Messages
          </button>

          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {message.subject || "(No Subject)"}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>From: {message.fromName || message.fromEmail}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(message.receivedAt)}</span>
                </div>
                {message.hasAttachments && (
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    <span>{message.attachments?.length || 0} attachments</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleReanalyze}
              disabled={analyzing}
              className="flex items-center gap-2 px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${analyzing ? "animate-spin" : ""}`}
              />
              Re-analyze
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Details */}
            <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-400 block mb-1">
                    From
                  </label>
                  <p className="text-gray-200">
                    {message.fromName} &lt;{message.fromEmail}&gt;
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400 block mb-2">
                    To
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {message.toEmails?.map((to, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-200 border border-gray-600"
                      >
                        {to.name || to.email}
                      </span>
                    ))}
                  </div>
                </div>

                {message.ccEmails?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 block mb-2">
                      CC
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {message.ccEmails.map((cc, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-200 border border-gray-600"
                        >
                          {cc.name || cc.email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-400 block mb-1">
                    Sent
                  </label>
                  <p className="text-gray-200">{formatDate(message.sentAt)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-400 block mb-1">
                    Received
                  </label>
                  <p className="text-gray-200">
                    {formatDate(message.receivedAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Message Content
              </h3>
              <div className="email-content-wrapper">
                <style>
                  {`
                    .email-content-wrapper {
                      all: initial;
                      display: block;
                      font-family: inherit;
                    }
                    .email-content-wrapper * {
                      color: #d1d5db !important;
                      background-color: transparent !important;
                      border-color: #4b5563 !important;
                      max-width: 100%;
                    }
                    .email-content-wrapper a {
                      color: #a78bfa !important;
                      text-decoration: underline;
                    }
                    .email-content-wrapper p,
                    .email-content-wrapper div,
                    .email-content-wrapper span {
                      line-height: 1.6;
                      margin-bottom: 1rem;
                    }
                    .email-content-wrapper img {
                      max-width: 100%;
                      height: auto;
                    }
                    .email-content-wrapper table {
                      border-collapse: collapse;
                      width: 100%;
                    }
                    .email-content-wrapper th,
                    .email-content-wrapper td {
                      padding: 0.5rem;
                      border: 1px solid #4b5563 !important;
                    }
                  `}
                </style>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      message.bodyContent ||
                      message.bodyPreview ||
                      "<p style='color: #d1d5db;'>No content available</p>",
                  }}
                />
              </div>
            </div>

            {/* Attachments */}
            {message.attachments?.length > 0 && (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Attachments ({message.attachments.length})
                </h3>
                <div className="space-y-2">
                  {message.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 bg-[#0C0E12] border border-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Paperclip className="w-5 h-5 text-gray-300" />
                        <div>
                          <p className="font-medium text-gray-200">
                            {attachment.filename}
                          </p>
                          <p className="text-sm text-gray-400">
                            {attachment.contentType}
                            {attachment.sizeBytes &&
                              ` • ${(attachment.sizeBytes / 1024).toFixed(
                                1
                              )} KB`}
                          </p>
                        </div>
                      </div>
                      {attachment.scanResult && (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            attachment.scanResult === "clean"
                              ? "bg-green-500/10 text-green-400"
                              : attachment.scanResult === "malicious"
                              ? "bg-red-500/10 text-red-400"
                              : "bg-orange-500/10 text-orange-400"
                          }`}
                        >
                          {attachment.scanResult}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entities */}
            {message.entities?.length > 0 && (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Detected Entities
                </h3>
                <div className="space-y-3">
                  {message.entities.map((entity) => (
                    <div
                      key={entity.id}
                      className="p-3 bg-[#0C0E12] border border-gray-700 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-200">
                          {entity.entityType}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(entity.confidence * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                      <p className="font-medium text-gray-200 mb-1">
                        {entity.entityValue}
                      </p>
                      {entity.context && (
                        <p className="text-sm text-gray-300">
                          {entity.context}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Analysis */}
          <div className="space-y-6">
            {message.analysis ? (
              <>
                {/* Priority & Risk */}
                <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Analysis Overview
                  </h3>
                  <div className="space-y-4">
                    {message.analysis.priority && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 block mb-2">
                          Priority
                        </label>
                        <div
                          className={`px-4 py-3 rounded-lg border ${getPriorityColor(
                            message.analysis.priority
                          )}`}
                        >
                          <span className="font-semibold capitalize">
                            {message.analysis.priority}
                          </span>
                        </div>
                      </div>
                    )}

                    {message.analysis.riskLevel && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 block mb-2">
                          Risk Level
                        </label>
                        <div
                          className={`px-4 py-3 rounded-lg border ${getRiskColor(
                            message.analysis.riskLevel
                          )}`}
                        >
                          <span className="font-semibold capitalize">
                            {message.analysis.riskLevel}
                          </span>
                        </div>
                      </div>
                    )}

                    {message.analysis.category && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 block mb-2">
                          Category
                        </label>
                        <span className="px-4 py-2 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400 inline-block">
                          {message.analysis.category.replace(/_/g, " ")}
                        </span>
                      </div>
                    )}

                    {message.analysis.sentiment && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 block mb-2">
                          Sentiment
                        </label>
                        <div className="flex items-center gap-3 p-3 bg-[#0C0E12] border border-gray-700 rounded-lg">
                          <span className="text-4xl">
                            {getSentimentIcon(message.analysis.sentiment)}
                          </span>
                          <div>
                            <p className="font-semibold text-gray-200 capitalize">
                              {message.analysis.sentiment.replace(/_/g, " ")}
                            </p>
                            {message.analysis.sentimentScore !== undefined && (
                              <p className="text-sm text-gray-400">
                                Score:{" "}
                                {message.analysis.sentimentScore.toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {message.sentiment?.tone && (
                      <div>
                        <label className="text-sm font-medium text-gray-400 block mb-2">
                          Tone
                        </label>
                        <span className="px-4 py-2 rounded-full text-sm font-medium bg-gray-700/50 text-gray-200 border border-gray-600 inline-block">
                          {message.sentiment.tone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {message.analysis.summary && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      AI Summary
                    </h3>
                    <p className="text-gray-200 leading-relaxed">
                      {message.analysis.summary}
                    </p>
                  </div>
                )}

                {/* Risk Factors */}
                {message.analysis.riskFactors?.length > 0 && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      Risk Factors
                    </h3>
                    <ul className="space-y-2">
                      {message.analysis.riskFactors.map((factor, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-gray-200"
                        >
                          <span className="text-orange-400 mt-1">•</span>
                          <span>{factor}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Compliance Flags */}
                {message.analysis.complianceFlags?.length > 0 && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-red-400" />
                      Compliance Flags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {message.analysis.complianceFlags.map((flag) => (
                        <span
                          key={flag}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400"
                        >
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Topics */}
                {message.analysis.keyTopics?.length > 0 && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Key Topics
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {message.analysis.keyTopics.map((topic) => (
                        <span
                          key={topic}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-200 border border-gray-600"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Items */}
                {message.analysis.actionItems?.length > 0 && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Action Items
                    </h3>
                    <ul className="space-y-2">
                      {message.analysis.actionItems.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            className="mt-1 rounded border-gray-600 text-purple-600 focus:ring-purple-500 bg-[#0C0E12]"
                          />
                          <span className="text-gray-200">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Suggested Labels */}
                {message.analysis.suggestedLabels?.length > 0 && (
                  <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Suggested Labels
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {message.analysis.suggestedLabels.map((label) => (
                        <span
                          key={label}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-200"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-[#13161B] border border-gray-700 rounded-lg p-6">
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-300 mb-4">
                    This email hasn't been analyzed yet
                  </p>
                  <button
                    onClick={handleReanalyze}
                    disabled={analyzing}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    Analyze Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetail;
